import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import RoleTable from "@/components/tables/RoleTable";
import TableHead from "@/components/tables/TableHead";
import { fetchRoles } from "@/services/role.service";
import { BUTTONS } from "@/utils/constants/appConstants/commonConstants";
import { ALL_ROLES } from "@/utils/constants/constants";
import { storeInLocalStorage } from "@/utils/functions/helperFunctions";
import { isFeaturePermitted } from "@/utils/functions/rbac";
import { useDataQueries } from "@/utils/hooks/useDataQueries";
import { getListCacheBootstrap, isListFullyFetched, writeListCache } from "@/utils/listPageCache";

const RoleList = () => {
    const [pageNo, setPageNo, dataLength, setDataLength, sorting, setSorting] = useDataQueries();
    const listBootstrap = getListCacheBootstrap("roles", { dataLength, sorting });

    const [isLoading, setIsLoading] = useState(!listBootstrap.hasCache);
    const [roles, setRoles] = useState<any>(listBootstrap.items);
    const [rolesLeft, setRolesLeft] = useState(true);
    const [allFetched, setAllFetched] = useState(false);
    const [dataLoad, setDataLoad] = useState(!listBootstrap.hasCache);
    const [totalCount, setTotalCount] = useState(listBootstrap.totalCount);

    const handleFetchRole = () => {
        if (rolesLeft) {
            setIsLoading(true);
            fetchRoles(
                pageNo,
                dataLength,
                sorting,
                (response: any) => {
                    setDataLoad(false);
                    setIsLoading(false);
                    setTotalCount(response.data.total_count);
                    if (response.data.roles.length > 0) {
                        const new_roles = response.data.roles;
                        setRoles(new_roles);
                        storeInLocalStorage(ALL_ROLES, new_roles);
                    } else {
                        setAllFetched(true);
                        setDataLoad(false);
                        setRolesLeft(false);
                        setIsLoading(false);
                    }
                },
                (error: any) => {
                    setDataLoad(false);
                    console.log(error);
                    setIsLoading(false);
                }
            );
        }
    };

    useEffect(() => {
        window.scrollTo(0, 0);
        const { cacheKey, cached } = getListCacheBootstrap("roles", { dataLength, sorting });

        setPageNo(1);
        setAllFetched(false);
        setRolesLeft(true);
        if (cached) {
            setRoles(cached.items);
            setTotalCount(cached.totalCount);
            setDataLoad(false);
            setIsLoading(false);
            if (isListFullyFetched(cached.items.length, cached.totalCount, dataLength as number)) {
                setAllFetched(true);
                setRolesLeft(false);
            }
        } else {
            setIsLoading(true);
            setDataLoad(true);
        }

        fetchRoles(
            1,
            dataLength,
            sorting,
            (response: any) => {
                const nextRoles = response.data.roles ?? [];
                writeListCache(cacheKey, nextRoles, response.data.total_count);
                setDataLoad(false);
                setIsLoading(false);
                setTotalCount(response.data.total_count);
                if (nextRoles.length > 0) {
                    setRoles(nextRoles);
                    storeInLocalStorage(ALL_ROLES, nextRoles);
                    if (isListFullyFetched(nextRoles.length, response.data.total_count, dataLength as number)) {
                        setAllFetched(true);
                        setRolesLeft(false);
                    }
                } else {
                    setAllFetched(true);
                    setRolesLeft(false);
                }
            },
            (error: any) => {
                setDataLoad(false);
                console.log(error);
                setIsLoading(false);
            }
        );
    }, [dataLength, sorting]);

    window.onscroll = function () {
        if (allFetched || isLoading) {
            return;
        }
        if (window.innerHeight + document.documentElement.scrollTop >= document.documentElement.offsetHeight - 1) {
            setPageNo(pageNo + 1);
            setIsLoading(true);
            if (rolesLeft) {
                fetchRoles(
                    pageNo + 1,
                    dataLength,
                    sorting,
                    (response: any) => {
                        setIsLoading(false);
                        setTotalCount(response.data.total_count);
                        if (response.data.roles.length > 0) {
                            const new_roles = [...roles, ...response.data.roles];
                            setRoles(new_roles);
                            storeInLocalStorage(ALL_ROLES, new_roles);
                            if (isListFullyFetched(new_roles.length, response.data.total_count, dataLength as number)) {
                                setAllFetched(true);
                                setRolesLeft(false);
                            }
                        } else {
                            setRolesLeft(false);
                            setAllFetched(true);
                            setIsLoading(false);
                        }
                    },
                    (error: any) => {
                        console.log(error);
                        setIsLoading(false);
                    }
                );
            }
        }
    };

    const [isCreateRolePermitted, setIsCreateRolePermission] = useState(false);
    const [isDeleteRolePermitted, setIsDeleteRolePermitted] = useState(false);

    useEffect(() => {
        setIsCreateRolePermission(isFeaturePermitted("AdminPortalCreateRole"));
        setIsDeleteRolePermitted(isFeaturePermitted("AdminPortalDeleteRole"));
    }, []);
    return (
        <div className="gs-list-page-card">
            <div className="gs-list-page-card-header">
                <TableHead
                    id='role-table'
                    pageNo={pageNo as number}
                    dataLength={dataLength as number}
                    setDataLength={setDataLength as (dataLength: number) => void}
                    title="Roles"
                    button={
                        isCreateRolePermitted && (
                            <Button asChild id='create-role-btn'>
                                <Link to="/role">
                                    {BUTTONS.CREATE}
                                </Link>
                            </Button>
                        )
                    }
                    currentEnvo={roles.length}
                    totalEnvo={totalCount}
                />
            </div>
            <div>
                <RoleTable id='role-table' setRoles={setRoles} isLoading={isLoading} dataLength={dataLength as number} sorting={sorting} setSorting={setSorting as ({}) => void} allFetched={allFetched} roles={roles} setPageNo={setPageNo} dataLoad={dataLoad} />
            </div>
        </div>
    );
};

export default RoleList;
